import { useRef } from 'react'
import { useInView, useReducedMotion } from 'motion/react'
import { cn } from '@/lib/utils'
import { HeroReveal, TextReveal } from '@/components/motion'
import { OfficeAppIcon, type OfficeApp } from '@/components/landing/OfficeAppIcon'
import { RotatedProcessCards } from '@/components/landing/service'
import { Highlight } from '@/components/landing/about/Highlight'
import { BenefitScrollCards } from './BenefitScrollCards'
import { WordPolishMedia } from './WordPolishMedia'
import { PlatformCtaBand, PlatformFaq, TrialButton, hairline, ink, splitLastWord, type LayoutProps } from './shared'

const APPS: Array<{ app: OfficeApp; name: string }> = [
  { app: 'word', name: 'Word' },
  { app: 'excel', name: 'Excel' },
  { app: 'powerpoint', name: 'PowerPoint' },
  { app: 'outlook', name: 'Outlook' },
]

function AppBadge({ app, name }: { app: OfficeApp; name: string }) {
  return (
    <span className={cn('flex shrink-0 items-center gap-3', ink)}>
      <OfficeAppIcon app={app} className="h-10 w-10" />
      <span className="font-[family-name:var(--font-landing-subdisplay)] text-2xl font-semibold tracking-[-0.01em] md:text-3xl">{name}</span>
    </span>
  )
}

/** Faded label left, marquee of the four Office apps right (about logo strip). */
function OfficeStrip({ label }: { label: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref)
  const reduce = useReducedMotion()
  return (
    <div className="grid items-center gap-6 lg:grid-cols-[minmax(0,0.25fr)_minmax(0,1fr)] lg:gap-12">
      <p className="landing-text-body-lg max-w-[24ch] text-[color-mix(in_srgb,var(--color-landing-gray-900)_66%,transparent)] dark:text-[color-mix(in_srgb,white_62%,transparent)]">
        {label}
      </p>
      {reduce ? (
        <ul className="flex flex-wrap gap-x-12 gap-y-5">
          {APPS.map((a) => (
            <li key={a.app}>
              <AppBadge {...a} />
            </li>
          ))}
        </ul>
      ) : (
        <div ref={ref} className="overflow-hidden py-2 [mask-image:linear-gradient(to_right,transparent,#000_12%,#000_88%,transparent)]">
          <ul className="sr-only">
            {APPS.map((a) => (
              <li key={a.app}>{a.name}</li>
            ))}
          </ul>
          <div aria-hidden className="animate-marquee flex w-max" style={{ animationDuration: '18s', animationPlayState: inView ? 'running' : 'paused' }}>
            {[0, 1, 2, 3].map((copy) => (
              <div key={copy} className="flex shrink-0 items-center gap-16 pr-16">
                {APPS.map((a) => (
                  <AppBadge key={`${copy}-${a.app}`} {...a} />
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Productivity ("statement", about reference): centred statement hero with
 * inline highlight boxes and a quote + button row; a full-width 16:9 Word
 * window where the task pane polishes the document; the Office app strip with
 * the figures; capabilities as rotated process cards; benefits as blue scroll
 * cards; FAQ; pink CTA.
 */
export function ProductivityLayout({ feature }: LayoutProps) {
  const [l1Lead, l1Last] = splitLastWord(feature.heroLines[0])
  const [l2Lead, l2Last] = splitLastWord(feature.heroLines[1])
  const [vpLead, vpLast] = splitLastWord(feature.valueProposition.title)

  return (
    <>
      {/* 1. Statement hero */}
      <section aria-labelledby="productivity-title" className="pb-[clamp(3rem,8vh,6rem)] pt-[clamp(4rem,12vh,8rem)]">
        <div className="landing-container">
          <div className="mx-auto max-w-[60rem]">
            <HeroReveal delay={0.05}>
              <h1 id="productivity-title" className={cn('landing-text-display-xl text-center', ink)}>
                {l1Lead} <Highlight color="pink">{l1Last}</Highlight> {l2Lead} <Highlight color="blue">{l2Last}</Highlight>
              </h1>
            </HeroReveal>
            <HeroReveal delay={0.2}>
              <div className="mt-12 flex flex-col gap-10 lg:mt-16 lg:flex-row lg:items-center lg:justify-between lg:gap-24">
                <p className="landing-text-body-lg max-w-[56ch] border-l border-[var(--color-landing-gray-300)] pl-5 text-[color-mix(in_srgb,var(--color-landing-gray-900)_66%,transparent)] dark:border-[var(--color-landing-gray-700)] dark:text-[color-mix(in_srgb,white_62%,transparent)]">
                  {feature.subheadline}
                </p>
                <TrialButton className="self-start lg:self-auto" />
              </div>
            </HeroReveal>
          </div>
        </div>
      </section>

      {/* 2. 16:9 media: the task pane at work in Word */}
      <HeroReveal delay={0.3} distance={40}>
        <WordPolishMedia feature={feature} headingId="productivity-media-title" />
      </HeroReveal>

      {/* 3. Strip: the four Office apps, then the figures */}
      <section aria-label={`${feature.shortLabel} in numbers`} className="landing-section-sm">
        <div className="landing-container">
          <OfficeStrip label={`${feature.shortLabel} tools for the four Office apps.`} />
          <dl className={cn('mt-12 grid gap-8 border-t pt-10 sm:grid-cols-3', hairline)}>
            {feature.heroStats.map((stat) => (
              <div key={stat.label} className="flex flex-col-reverse justify-end gap-2">
                <dt className="border-l-2 border-[#ff00f2] pl-3 text-base font-bold uppercase tracking-[0.01em] text-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-300)]">
                  {stat.label}
                </dt>
                <dd className={cn('font-[family-name:var(--font-landing-display)] text-[clamp(2.5rem,5vw,4.5rem)] uppercase leading-[1.25] tracking-[-0.03em]', ink)}>
                  {stat.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* 4. Capabilities as rotated process cards */}
      <section aria-labelledby="productivity-capabilities-title" className="landing-section overflow-x-clip">
        <div className="landing-container">
          <TextReveal as="h2" className={cn('landing-text-display-xl max-w-[22ch]', ink)}>
            <span id="productivity-capabilities-title">
              One add-in for each <Highlight color="purple">app.</Highlight>
            </span>
          </TextReveal>
          <RotatedProcessCards
            className="mt-16 lg:mt-8"
            cards={feature.capabilities.map((c) => ({ title: c.title, description: c.description }))}
          />
        </div>
      </section>

      {/* 5. Benefits as blue scroll cards */}
      <BenefitScrollCards
        headingId="productivity-benefits-title"
        heading={
          <>
            {vpLead} <span className="landing-highlight bg-[#ff00f2]">{vpLast}</span>
          </>
        }
        body={feature.valueProposition.description}
        cards={feature.benefits}
      />

      <PlatformFaq feature={feature} />
      <PlatformCtaBand feature={feature} tone="pink" />
    </>
  )
}
